import React from 'react';
import Table from '../../../../components/Table/Table';
import constants from '../../../../utils/constants';
import {
  uriGetDefinition,
  uriPauseDefinition,
  uriResumeDefinition,
  uriRestartDefinition,
  uriRestartTask
} from '../../../../utils/endpoints';
import ConfirmModal from '../../../../components/Modal/ConfirmModal/ConfirmModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AceEditor from '../../../../components/AceEditor/AceEditor';
import Root from '../../../../components/Root';
import { withRouter } from '../../../../utils/withRouter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay, faRefresh } from '@fortawesome/free-solid-svg-icons';

class ConnectTasks extends Root {
  state = {
    clusterId: this.props.clusterId || this.props.params.clusterId,
    connectId: this.props.connectId || this.props.params.connectId,
    definitionId: this.props.definitionId || this.props.params.clusterId,
    definition: {},
    definitionModifyMessage: '',
    definitionModifyData: {},
    tableData: [],
    showActionModal: false,
    actionMessage: '',
    roles: JSON.parse(sessionStorage.getItem('roles')),
    loading: true
  };

  definitionState = {
    PAUSE: 'PAUSE',
    RESUME: 'RESUME',
    RESTART: 'RESTART',
    RESTART_TASK: 'RESTART_TASK'
  };

  componentDidMount() {
    this.getDefinition();
  }

  handleTasks() {
    const tasks = this.state.definition.tasks || [];
    let tableData = [];
    tasks.forEach(task => {
      tableData.push({
        id: JSON.stringify(task.id),
        worker: task.workerId,
        state: task.state,
        trace: task.trace
      });
    });
    this.setState({ tableData, loading: false });
  }

  async getDefinition() {
    let definition = {};
    const { clusterId, connectId, definitionId } = this.state;

    this.setState({ loading: true });
    definition = await this.getApi(uriGetDefinition(clusterId, connectId, definitionId));
    this.setState({ definition: definition.data }, () => this.handleTasks());
  }

  static POLL_INTERVAL = 1000;
  static POLL_MAX_ATTEMPTS = 10;

  async waitForStateChange(expectedAction, taskId) {
    const { clusterId, connectId, definitionId } = this.state;

    // For individual task restarts, just do a single delayed refresh
    if (taskId !== undefined) {
      await new Promise(resolve => setTimeout(resolve, ConnectTasks.POLL_INTERVAL));
      await this.getDefinition();
      return;
    }

    for (let i = 0; i < ConnectTasks.POLL_MAX_ATTEMPTS; i++) {
      await new Promise(resolve => setTimeout(resolve, ConnectTasks.POLL_INTERVAL));
      const response = await this.getApi(uriGetDefinition(clusterId, connectId, definitionId));
      const tasks = response.data.tasks || [];

      if (tasks.length === 0) continue;

      let settled = false;
      if (expectedAction === 'paused') {
        settled = tasks.every(t => t.state === 'PAUSED');
      } else if (expectedAction === 'resumed' || expectedAction === 'restarted') {
        settled = tasks.every(t => t.state === 'RUNNING' || t.state === 'FAILED');
      }

      if (settled) {
        this.setState({ definition: response.data }, () => this.handleTasks());
        return;
      }
    }

    // Exhausted attempts — refresh with whatever state we have
    await this.getDefinition();
  }

  modifyDefinitionState = () => {
    const { definitionId } = this.state;
    const { uri, action, taskId } = this.state.definitionModifyData;

    this.setState({ loading: true });
    this.getApi(uri)
      .then(() => this.waitForStateChange(action, taskId))
      .then(() => {
        toast.success(
          `${
            taskId !== undefined
              ? `Definition '${definitionId}' tasks ${taskId} is restarted`
              : `Definition '${definitionId}' is ${action}`
          }`
        );
        this.closeActionModal();
      })
      .catch(() => {
        toast.error(`Failed to ${this.state.definitionModifyData.failedAction} definition '${definitionId}'`);
        this.getDefinition();
        this.closeActionModal();
      });
  };

  showActionModal = definitionModifyMessage => {
    this.setState({ showActionModal: true, definitionModifyMessage });
  };

  closeActionModal = () => {
    this.setState({
      showActionModal: false,
      definitionModifyMessage: '',
      definitionModifyData: {}
    });
  };

  handleAction = (option, taskId) => {
    const { clusterId, connectId, definitionId } = this.state;
    let uri = '';
    let action = '';
    let failedAction = '';

    switch (option) {
      case 'PAUSE':
        uri = uriPauseDefinition(clusterId, connectId, definitionId);
        action = 'paused';
        failedAction = 'pause';
        break;
      case 'RESUME':
        uri = uriResumeDefinition(clusterId, connectId, definitionId);
        action = 'resumed';
        failedAction = 'resume';
        break;
      case 'RESTART':
        uri = uriRestartDefinition(clusterId, connectId, definitionId);
        action = 'restarted';
        failedAction = 'restart';
        break;
      case 'RESTART_TASK':
        uri = uriRestartTask(clusterId, connectId, definitionId, taskId);
        action = 'restarted';
        failedAction = 'restart';
        break;
      default:
        uri = uriResumeDefinition(clusterId, connectId, definitionId);
        action = 'resumed';
        failedAction = 'resume';
        break;
    }

    let definitionModifyData = {
      uri,
      action,
      failedAction
    };

    if (taskId !== undefined) definitionModifyData.taskId = taskId;

    this.setState({ definitionModifyData }, () => {
      this.showActionModal(
        taskId !== undefined ? (
          <React.Fragment>
            Do you want to restart task:{' '}
            {
              <code>
                {taskId} from {this.state.definitionId}
              </code>
            }{' '}
            ?
          </React.Fragment>
        ) : (
          <React.Fragment>
            Do you want to {failedAction} definition: {<code>{this.state.definitionId}</code>} ?
          </React.Fragment>
        )
      );
    });
  };

  renderTask = task => {
    let className = 'btn btn-sm mb-1 btn-';
    switch (task) {
      case 'RUNNING':
        className += 'success';
        break;
      case 'FAILED':
        className += 'danger';
        break;
      default:
        className += 'warning';
        break;
    }
    return <span className={`btn btn-sm mb-1 ${className}`}>{task}</span>;
  };

  render() {
    const { tableData, definition, loading } = this.state;
    const roles = this.state.roles || {};
    return (
      <div className="tab-pane active" role="tabpanel">
        <div className="table-responsive">
          <Table
            loading={loading}
            columns={[
              {
                id: 'id',
                name: 'id',
                accessor: 'id',
                colName: 'Id',
                type: 'text',
                sortable: true
              },
              {
                id: 'worker',
                name: 'worker',
                accessor: 'worker',
                colName: 'Worker',
                type: 'text',
                sortable: true
              },
              {
                id: 'state',
                name: 'state',
                accessor: 'state',
                colName: 'State',
                type: 'text',
                cell: (obj, col) => {
                  return this.renderTask(obj[col.accessor]);
                }
              },
              {
                id: 'trace',
                name: 'trace',
                accessor: 'trace',
                colName: 'Trace',
                type: 'text',
                extraRow: true,
                extraRowContent: (obj, index) => {
                  return (
                    <AceEditor
                      mode="text"
                      id={'value' + index}
                      value={obj.trace}
                      readOnly
                      style={{ width: '100%', minHeight: '25vh' }}
                    />
                  );
                },
                cell: obj => {
                  return obj.trace ? (
                    <pre className="mb-0 khq-data-highlight">
                      <code>{obj.trace}</code>
                    </pre>
                  ) : null;
                }
              }
            ]}
            extraRow
            noStripes
            data={tableData}
            updateData={data => {
              this.setState({ tableData: data });
            }}
            actions={
              roles.CONNECTOR &&
              roles.CONNECTOR.includes('UPDATE_STATE') && [constants.TABLE_RESTART]
            }
            onRestart={row => {
              this.handleAction(this.definitionState.RESTART_TASK, row.id);
            }}
          />
        </div>
        {roles.CONNECTOR && roles.CONNECTOR.includes('UPDATE_STATE') && (
          <aside>
            {definition.paused ? (
              <li className="aside-button">
                <div
                  //href="#"
                  className="btn btn-primary me-2"
                  onClick={() => this.handleAction(this.definitionState.RESUME)}
                >
                  <FontAwesomeIcon icon={faPlay} aria-hidden={true} /> Resume Definition
                </div>
              </li>
            ) : (
              <React.Fragment>
                <li className="aside-button">
                  <div
                    //href="#"
                    type="pause"
                    className="btn btn-primary me-2"
                    onClick={() => this.handleAction(this.definitionState.PAUSE)}
                  >
                    <FontAwesomeIcon icon={faPause} aria-hidden={true} /> Pause Definition
                  </div>
                </li>

                <li className="aside-button">
                  <div
                    //href="#"
                    className="btn btn-primary me-2"
                    onClick={() => this.handleAction(this.definitionState.RESTART)}
                  >
                    <FontAwesomeIcon icon={faRefresh} aria-hidden={true} /> Restart Definition
                  </div>
                </li>
              </React.Fragment>
            )}
          </aside>
        )}
        <ConfirmModal
          show={this.state.showActionModal}
          handleCancel={this.closeActionModal}
          handleConfirm={this.modifyDefinitionState}
          message={this.state.definitionModifyMessage}
        />
      </div>
    );
  }
}

export default withRouter(ConnectTasks);
